import { useEffect, useState, useCallback } from "react";
import Layout from "../components/Layout";
import HelpTip from "../components/Help/HelpTip";
import EmptyState from "../components/Help/EmptyState";
import { api } from "../lib/api";

import { useAuth } from "../lib/auth";
import { useEntity } from "../lib/entity";
import {
  Wrench, Plus, Filter, AlertTriangle, CheckCircle2, Clock,
  DollarSign, Camera, ExternalLink, Trash2, X, ChevronRight,
  Upload, Image as ImageIcon
} from "lucide-react";
import { toast } from "sonner";

const PRIORITIES = [
  { id: "urgent", label: "Urgent", badge: "bg-red-500/20 text-red-400 border border-red-500/40 pulse-urgent" },
  { id: "high", label: "High", badge: "bg-amber-500/20 text-amber-400 border border-amber-500/40" },
  { id: "medium", label: "Medium", badge: "bg-blue-500/20 text-blue-400 border border-blue-500/40" },
  { id: "low", label: "Low", badge: "bg-slate-500/20 text-slate-400 border border-slate-500/40" },
];

const STATUSES = [
  { id: "open", label: "Open", color: "text-amber-400 bg-amber-500/10 border-amber-500/30" },
  { id: "in_progress", label: "In Progress", color: "text-blue-400 bg-blue-500/10 border-blue-500/30" },
  { id: "resolved", label: "Resolved", color: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30" },
];

export default function Maintenance() {
  const { can } = useAuth();
  const { activeEntityId } = useEntity();
  const [requests, setRequests] = useState([]);
  const [properties, setProperties] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [propertyFilter, setPropertyFilter] = useState("all");

  // Modals
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [resolveModalTarget, setResolveModalTarget] = useState(null);
  const [photoUploadTarget, setPhotoUploadTarget] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    property_id: "",
    title: "",
    description: "",
    priority: "medium",
    estimated_cost: "",
    scheduled_date: "",
  });

  const [resolveData, setResolveData] = useState({
    actual_cost: "",
    vendor_id: "",
    notes: "",
  });

  const [uploadingPhotos, setUploadingPhotos] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeEntityId && activeEntityId !== "all") params.entity_id = activeEntityId;
      if (statusFilter !== "all") params.status = statusFilter;
      if (priorityFilter !== "all") params.priority = priorityFilter;
      if (propertyFilter !== "all") params.property_id = propertyFilter;

      const [mRes, pRes, vRes] = await Promise.all([
        api.get("/maintenance", { params }),
        api.get("/properties", { params: activeEntityId !== "all" ? { entity_id: activeEntityId } : {} }),
        api.get("/vendors"),
      ]);
      setRequests(mRes.data || []);
      setProperties(pRes.data || []);
      setVendors(vRes.data || []);
    } catch (err) {
      toast.error("Failed to load maintenance requests");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [activeEntityId, statusFilter, priorityFilter, propertyFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.property_id || !formData.title) {
      toast.error("Please fill in required fields");
      return;
    }
    try {
      await api.post("/maintenance", {
        ...formData,
        estimated_cost: parseFloat(formData.estimated_cost) || 0,
      });
      toast.success("Maintenance request created");
      setCreateModalOpen(false);
      setFormData({
        property_id: "",
        title: "",
        description: "",
        priority: "medium",
        estimated_cost: "",
        scheduled_date: "",
      });
      loadData();
    } catch (err) {
      toast.error("Failed to create request");
    }
  };

  const handleStatusChange = async (mid, newStatus) => {
    try {
      await api.put(`/maintenance/${mid}`, { status: newStatus });
      toast.success(`Status updated to ${newStatus}`);
      loadData();
    } catch {
      toast.error("Failed to update status");
    }
  };

  const handleResolveAndLink = async (e) => {
    e.preventDefault();
    if (!resolveModalTarget) return;
    try {
      await api.post(`/maintenance/${resolveModalTarget.id}/resolve-and-link-tx`, {
        actual_cost: parseFloat(resolveData.actual_cost) || 0,
        vendor_id: resolveData.vendor_id || null,
        notes: resolveData.notes,
      });
      toast.success("Resolved & linked to Expense transaction in Books!");
      setResolveModalTarget(null);
      setResolveData({ actual_cost: "", vendor_id: "", notes: "" });
      loadData();
    } catch {
      toast.error("Failed to resolve and link transaction");
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = e.target.files;
    if (!files || !files.length || !photoUploadTarget) return;
    const form = new FormData();
    for (let i = 0; i < Math.min(files.length, 5); i++) {
      form.append("files", files[i]);
    }
    setUploadingPhotos(true);
    try {
      await api.post(`/maintenance/${photoUploadTarget.id}/photos`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Photos attached successfully");
      setPhotoUploadTarget(null);
      loadData();
    } catch {
      toast.error("Failed to upload photos");
    } finally {
      setUploadingPhotos(false);
    }
  };

  const handleDelete = async (mid) => {
    if (!window.confirm("Are you sure you want to delete this maintenance request?")) return;
    try {
      await api.delete(`/maintenance/${mid}`);
      toast.success("Maintenance request deleted");
      loadData();
    } catch {
      toast.error("Failed to delete request");
    }
  };

  // Metrics
  const openCount = requests.filter((r) => r.status === "open").length;
  const inProgressCount = requests.filter((r) => r.status === "in_progress").length;
  const urgentCount = requests.filter((r) => r.priority === "urgent" && r.status !== "resolved").length;
  const totalCost = requests.reduce((acc, r) => acc + (r.actual_cost || r.estimated_cost || 0), 0);

  return (
    <Layout
      title="Maintenance Tracker"
      subtitle="Work orders, repair jobs, contractor expenses & photos"
      actions={
        can.edit && (
          <button
            onClick={() => setCreateModalOpen(true)}
            data-testid="new-maintenance-btn"
            className="btn-primary text-xs sm:text-sm py-2 px-3 sm:px-4"
          >
            <Plus size={16} />
            <span>New Request</span>
          </button>
        )
      }
    >
      {/* Metric Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="glass-card p-4">
          <div className="text-[11px] uppercase tracking-wider text-[var(--muted)] font-semibold flex items-center gap-1.5">
            <Clock size={13} className="text-amber-400" /> Open Requests
          </div>
          <div className="text-2xl font-black tabular-nums mt-1">{openCount}</div>
          <div className="text-xs text-[var(--muted)] mt-0.5">Awaiting contractor</div>
        </div>

        <div className="glass-card p-4">
          <div className="text-[11px] uppercase tracking-wider text-[var(--muted)] font-semibold flex items-center gap-1.5">
            <Wrench size={13} className="text-blue-400" /> In Progress
          </div>
          <div className="text-2xl font-black tabular-nums mt-1">{inProgressCount}</div>
          <div className="text-xs text-[var(--muted)] mt-0.5">Active repairs</div>
        </div>

        <div className={`glass-card p-4 ${urgentCount > 0 ? "border-red-500/40" : ""}`}>
          <div className="text-[11px] uppercase tracking-wider text-[var(--muted)] font-semibold flex items-center gap-1.5">
            <AlertTriangle size={13} className="text-red-400" /> Urgent / Emergency
          </div>
          <div className="text-2xl font-black tabular-nums text-red-400 mt-1">{urgentCount}</div>
          <div className="text-xs text-[var(--muted)] mt-0.5">Requires immediate action</div>
        </div>

        <div className="glass-card p-4">
          <div className="text-[11px] uppercase tracking-wider text-[var(--muted)] font-semibold flex items-center gap-1.5">
            <DollarSign size={13} className="text-teal-400" /> Total Cost
          </div>
          <div className="text-2xl font-black tabular-nums text-teal-400 mt-1">
            ${totalCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div className="text-xs text-[var(--muted)] mt-0.5">Actual + estimated</div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="card p-4 mb-6 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5 text-xs text-[var(--muted)] font-semibold uppercase tracking-wider">
          <Filter size={14} /> Filter:
        </div>

        {/* Status Pills */}
        <div className="segmented">
          {["all", "open", "in_progress", "resolved"].map((st) => (
            <button
              key={st}
              onClick={() => setStatusFilter(st)}
              className={statusFilter === st ? "active" : ""}
            >
              {st === "all" ? "All Status" : st === "in_progress" ? "In Progress" : st.charAt(0).toUpperCase() + st.slice(1)}
            </button>
          ))}
        </div>

        {/* Priority Filter */}
        <div className="flex items-center gap-1.5">
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="lapen-input !w-auto text-xs py-1.5"
          >
            <option value="all">All Priorities</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <HelpTip topic="maintenance-tracker" />
        </div>

        {/* Property Filter */}
        <select
          value={propertyFilter}
          onChange={(e) => setPropertyFilter(e.target.value)}
          className="lapen-input !w-auto text-xs py-1.5"
        >
          <option value="all">All Properties</option>
          {properties.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </div>

      {/* Requests List */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card p-5 h-28 skeleton" />
          ))}
        </div>
      ) : requests.length === 0 ? (
        <EmptyState
          icon={Wrench}
          title={
            statusFilter !== "all" || priorityFilter !== "all" || propertyFilter !== "all"
              ? "No Matching Maintenance Requests"
              : "No Maintenance Requests Found"
          }
          description={
            statusFilter !== "all" || priorityFilter !== "all" || propertyFilter !== "all"
              ? "No work orders match the selected filters. Adjust or reset your filters, or log a new work order."
              : "Track tenant repair tickets, prioritize habitability issues, attach photos, and automatically link contractor expenses."
          }
          helpTopic="maintenance-tracker"
          helpText="About Maintenance & Habitability Laws"
          actionLabel="+ Log Work Order"
          onAction={() => setCreateModalOpen(true)}
          secondaryActionLabel={
            statusFilter !== "all" || priorityFilter !== "all" || propertyFilter !== "all"
              ? "Reset All Filters"
              : null
          }
          onSecondaryAction={() => {
            setStatusFilter("all");
            setPriorityFilter("all");
            setPropertyFilter("all");
          }}
          testId="maint-empty-state"
        />
      ) : (

        <div className="space-y-3.5">
          {requests.map((r) => {
            const pBadge = PRIORITIES.find((p) => p.id === r.priority) || PRIORITIES[2];
            const sBadge = STATUSES.find((s) => s.id === r.status) || STATUSES[0];
            const photos = (() => {
              try {
                return JSON.parse(r.photos || "[]");
              } catch {
                return [];
              }
            })();

            return (
              <div
                key={r.id}
                className={`card card-hover p-5 relative overflow-hidden ${
                  r.priority === "urgent" && r.status !== "resolved" ? "border-red-500/50" : ""
                }`}
                data-testid={`maint-card-${r.id}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1.5">
                      <span className={`tag ${pBadge.badge}`}>{pBadge.label}</span>
                      <span className={`tag border ${sBadge.color}`}>{sBadge.label}</span>
                      <span className="text-xs text-slate-600 dark:text-slate-400 font-medium">
                        {r.property_name || "Unknown Property"}
                      </span>
                      {r.tenant_name && (
                        <span className="text-xs text-slate-600 dark:text-slate-400 font-medium">
                          · Tenant: <strong className="text-[var(--text)]">{r.tenant_name}</strong>
                        </span>
                      )}
                    </div>

                    <h3 className="text-base sm:text-lg font-bold">{r.title}</h3>
                    {r.description && (
                      <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mt-1 whitespace-pre-line leading-relaxed">
                        {r.description}
                      </p>
                    )}

                    {/* Photos Preview */}
                    {photos.length > 0 && (
                      <div className="flex items-center gap-2 mt-3 flex-wrap">
                        {photos.map((ph, idx) => (
                          <a
                            key={idx}
                            href={`/data/${ph}`}
                            target="_blank"
                            rel="noreferrer"
                            className="w-12 h-12 rounded-lg overflow-hidden border border-[var(--border)] bg-black/20 hover:opacity-80 transition-opacity"
                            title="View full photo"
                          >
                            <img src={`/data/${ph}`} alt="repair" className="w-full h-full object-cover" />
                          </a>
                        ))}
                      </div>
                    )}

                    {/* Cost & Date Row */}
                    <div className="flex items-center gap-4 mt-3 text-xs text-slate-600 dark:text-slate-400 font-medium flex-wrap">
                      {r.estimated_cost > 0 && (
                        <span>
                          Estimated: <strong className="text-[var(--text)] tabular-nums">${r.estimated_cost.toFixed(2)}</strong>
                        </span>
                      )}
                      {r.actual_cost > 0 && (
                        <span className="text-teal-600 dark:text-teal-400 font-semibold">
                          Actual Cost: <strong className="tabular-nums">${r.actual_cost.toFixed(2)}</strong>
                        </span>
                      )}
                      {r.scheduled_date && (
                        <span>Scheduled: {new Date(r.scheduled_date).toLocaleDateString()}</span>
                      )}
                      {r.transaction_id && (
                        <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-medium">
                          <CheckCircle2 size={12} /> Linked to Expense
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  {can.edit && (
                    <div className="flex items-center gap-2 shrink-0 pt-2 sm:pt-0 border-t sm:border-t-0 border-[var(--border)]">
                      <button
                        onClick={() => setPhotoUploadTarget(r)}
                        className="p-2 rounded-xl border border-[var(--border)] text-[var(--text-secondary)] hover:text-teal-400 transition-colors"
                        title="Attach photos"
                      >
                        <Camera size={16} />
                      </button>

                      {r.status === "open" && (
                        <button
                          onClick={() => handleStatusChange(r.id, "in_progress")}
                          className="btn-secondary text-xs py-1.5 px-3"
                        >
                          Start Work
                        </button>
                      )}

                      {r.status !== "resolved" && (
                        <button
                          onClick={() => {
                            setResolveModalTarget(r);
                            setResolveData({
                              actual_cost: r.estimated_cost || "",
                              vendor_id: "",
                              notes: "",
                            });
                          }}
                          className="btn-primary text-xs py-1.5 px-3"
                        >
                          <CheckCircle2 size={14} /> Resolve & Link
                        </button>
                      )}

                      {can.admin && (
                        <button
                          onClick={() => handleDelete(r.id)}
                          className="p-2 rounded-xl text-red-400 hover:bg-red-500/10 transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* New Request Modal */}
      {createModalOpen && (
        <div className="sheet-backdrop" onClick={() => setCreateModalOpen(false)}>
          <div className="sheet-content" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-[var(--border)] pb-4 mb-4">
              <h2 className="text-lg font-bold">New Maintenance Request</h2>
              <button onClick={() => setCreateModalOpen(false)} className="p-1 rounded-lg hover:bg-[var(--surface-hover)]">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="label">Property *</label>
                <select
                  value={formData.property_id}
                  onChange={(e) => setFormData({ ...formData, property_id: e.target.value })}
                  required
                  className="lapen-input"
                >
                  <option value="">Select a property...</option>
                  {properties.map((p) => (
                    <option key={p.id} value={p.id}>{p.name} ({p.address})</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="label">Issue Title *</label>
                <input
                  type="text"
                  placeholder="e.g. Water heater leak, HVAC failure, Lock replacement"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="lapen-input"
                />
              </div>

              <div>
                <label className="label">Description / Contractor Notes</label>
                <textarea
                  rows={3}
                  placeholder="Details of the problem, tenant report, contractor contact..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="lapen-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Priority Level</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    className="lapen-input"
                  >
                    <option value="low">Low (Cosmetic / routine)</option>
                    <option value="medium">Medium (Standard repair)</option>
                    <option value="high">High (Urgent tenant impact)</option>
                    <option value="urgent">Urgent (Flooding / electrical / no heat)</option>
                  </select>
                </div>

                <div>
                  <label className="label">Estimated Cost ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={formData.estimated_cost}
                    onChange={(e) => setFormData({ ...formData, estimated_cost: e.target.value })}
                    className="lapen-input tabular-nums"
                  />
                </div>
              </div>

              <div>
                <label className="label">Scheduled Repair Date</label>
                <input
                  type="date"
                  value={formData.scheduled_date}
                  onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                  className="lapen-input"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-[var(--border)]">
                <button
                  type="button"
                  onClick={() => setCreateModalOpen(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Create Request
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Resolve & Link to Transaction Modal */}
      {resolveModalTarget && (
        <div className="sheet-backdrop" onClick={() => setResolveModalTarget(null)}>
          <div className="sheet-content" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-[var(--border)] pb-4 mb-4">
              <div>
                <h2 className="text-lg font-bold">Resolve & Link to Expense</h2>
                <div className="text-xs text-[var(--muted)]">{resolveModalTarget.title}</div>
              </div>
              <button onClick={() => setResolveModalTarget(null)} className="p-1 rounded-lg hover:bg-[var(--surface-hover)]">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleResolveAndLink} className="space-y-4">
              <div className="p-3.5 rounded-xl bg-teal-500/10 border border-teal-500/20 text-xs text-teal-300">
                Resolving this request will automatically record an <strong>Expense transaction</strong> under the <strong>Repairs</strong> category in your books for IRS Schedule E tax deductions.
              </div>

              <div>
                <label className="label">Actual Repair Cost ($) *</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  placeholder="0.00"
                  value={resolveData.actual_cost}
                  onChange={(e) => setResolveData({ ...resolveData, actual_cost: e.target.value })}
                  className="lapen-input tabular-nums font-bold text-base"
                />
              </div>

              <div>
                <label className="label">Vendor / Contractor</label>
                <select
                  value={resolveData.vendor_id}
                  onChange={(e) => setResolveData({ ...resolveData, vendor_id: e.target.value })}
                  className="lapen-input"
                >
                  <option value="">No vendor selected / paid directly</option>
                  {vendors.map((v) => (
                    <option key={v.id} value={v.id}>{v.name} ({v.category || "Contractor"})</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="label">Resolution Notes</label>
                <input
                  type="text"
                  placeholder="e.g. Installed new Bradford White 50-gal water heater"
                  value={resolveData.notes}
                  onChange={(e) => setResolveData({ ...resolveData, notes: e.target.value })}
                  className="lapen-input"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-[var(--border)]">
                <button
                  type="button"
                  onClick={() => setResolveModalTarget(null)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  <CheckCircle2 size={16} /> Mark Resolved & Post Expense
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Photo Upload Input Dialog */}
      {photoUploadTarget && (
        <div className="sheet-backdrop" onClick={() => setPhotoUploadTarget(null)}>
          <div className="sheet-content" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-[var(--border)] pb-4 mb-4">
              <h2 className="text-lg font-bold">Attach Photos</h2>
              <button onClick={() => setPhotoUploadTarget(null)} className="p-1 rounded-lg hover:bg-[var(--surface-hover)]">
                <X size={18} />
              </button>
            </div>

            <div className="text-center py-8 border-2 border-dashed border-[var(--border)] rounded-2xl bg-[var(--surface-hover)]/40 p-6">
              <Camera size={36} className="text-teal-400 mx-auto mb-3" />
              <p className="text-sm font-semibold mb-1">Select repair or damage photos</p>
              <p className="text-xs text-[var(--muted)] mb-4">JPG, PNG, or WEBP up to 15MB each (max 5 photos)</p>

              <label className="btn-primary cursor-pointer">
                <Upload size={16} />
                <span>{uploadingPhotos ? "Uploading..." : "Choose Files"}</span>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  disabled={uploadingPhotos}
                  className="hidden"
                />
              </label>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
